import React, { useState } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useSelector } from "react-redux";
import {
  getJobopeningList,
  updateJobopening,
} from "../../../../services/workspace/recruitment.service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { usePermissionCheck } from "../../../../hooks/usePermissionCheck";

import { IoCheckmarkCircleOutline } from "react-icons/io5";
import DefaultButton from "../../../components/buttons/defaultButton";
import CommonModal from "../../../components/modals/commonModel";

const items = [{ label: "Job Opening", path: "" }];

const ManageJobOpening = () => {
  const { limit } = useSelector((state) => state.global);
  const navigate = useNavigate();
  const permissions = usePermissionCheck("recruitment", "job-opening");

  const customCase = {
    0: {
      label: "Draft",
      color: "text-blue-600 text-sm",
      bg: "bg-blue-600",
    },
    1: {
      label: "Open",
      color: "text-green-600 text-sm",
      bg: "bg-green-600",
    },
    2: {
      label: "Hold",
      color: "text-indigo-600 text-sm",
      bg: "bg-indigo-600",
    },
    3: {
      label: "Closed",
      color: "text-yellow-600 text-sm",
      bg: "bg-yellow-600",
    },
  };

  const [modal, setModal] = useState({
    show: false,
    title: "",
    message: "",
    actionType: null,
    item: null,
    loading: false,
    icon: null,
    confirmText: "Confirm",
    buttonVariant: "primary",
  });

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    handleSearchChange,
    handlePageChange,
    fetchData,
  } = useCommonFetch(getJobopeningList, {
    dataPath: "jobOpening.data",
    totalPath: "jobOpening.meta.total",
    errorMessage: "Failed to fetch job openings!",
    customParams: {
      with: "department",
    },
    debounceDelay: 500,

    dataTransformer: (data) => {
      if (!Array.isArray(data)) return { data: [], action: {} };

      const transformedData = data.map((item) => ({
        ...item,
        id: { data: item?.uuid, variant: "text" },
        name: { data: item?.title || "-", variant: "text" },
        department: { data: item?.department?.name || "-", variant: "text" },
        vacancies: { data: item?.no_of_vacancies || 0, variant: "number" },
        experience_required: {
          data: `${item?.minimum_experience ?? 0} - ${
            item?.maximum_experience ?? 0
          } Years`,
          variant: "text",
        },
        location: { data: item?.address || "-", variant: "text" },
        closing_date: {
          data:
            item?.closing_date && item?.closing_date !== "-"
              ? item?.closing_date
              : "-",
          variant:
            item?.closing_date && item?.closing_date !== "-" ? "date" : "text",
        },
        created_at: {
          data:
            item?.created_at && item?.created_at !== "-" ? item?.created_at : "-",
          variant:
            item?.created_at && item?.created_at !== "-" ? "date-time" : "text",
        },
        status: {
          data: item?.status,
          className: "",
          variant: "status",
          customCases: customCase, // assuming you already have this defined somewhere
        },
      }));

      const actionButton = {
        view: (item) =>
          navigate(`/recruitment/job-opening/detail/${item.id.data}`),
        // edit: (item) =>
        //   navigate(`/recruitment/job-opening/edit/${item.id.data}`),

        publish: (item) =>
          setModal({
            show: true,
            actionType: "publish",
            item,
            title: "Publish Job Opening",
            message: `Are you sure you want to publish "${item.name.data}"?`,
            icon: (
              <IoCheckmarkCircleOutline className="text-6xl text-green-600" />
            ),
            confirmText: "Publish",
            buttonVariant: "success",
          }),

        onhold: (item) =>
          setModal({
            show: true,
            actionType: "onhold",
            item,
            title: "Hold Job ",
            message: `Put "${item.name.data}" Job on hold? It will no longer be visible to candidates.`,
            confirmText: "Hold",
            buttonVariant: "warning",
          }),

        cancel: (item) =>
          setModal({
            show: true,
            actionType: "cancel",
            item,
            title: "Cancel Job Opening",
            message: `Cancel "${item.name.data}" permanently?`,
            confirmText: "Cancel Job",
            buttonVariant: "danger",
          }),

        reopen: (item) =>
          setModal({
            show: true,
            actionType: "reopen",
            item,
            title: "Reopen Job Opening",
            message: `Reopen "${item.name.data}" for new applications?`,
            confirmText: "Reopen",
            buttonVariant: "info",
          }),

        showPublish: (item) => item.status?.data !== 1,
        showOnhold: (item) => item.status?.data === 1,
        showCancel: (item) => [1, 2].includes(item.status?.data),
        showReopen: (item) => [2, 3].includes(item.status?.data),
      };

      return { data: transformedData, action: actionButton };
    },
  });

  const handleStatusConfirm = async () => {
    if (!modal.item) return;

    setModal((prev) => ({ ...prev, loading: true }));

    const statusMap = {
      publish: 1,
      draft: 0,
      open: 1,
      onhold: 2,
      cancel: 3,
      reopen: 1,
    };

    try {
      // await updateJobopening(payload, modal.item.id.data, {
      //   status: statusMap[modal.actionType],
      // });
      await updateJobopening(
        { status: statusMap[modal.actionType] },
        modal.item.id.data
      );

      toast.success(`${modal.confirmText} successful!`);
      fetchData();
      setModal({ show: false, item: null, actionType: null });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
      setModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const TableHeaderData = [
    { label: "Job Title", key: "name" },
    { label: "Department", key: "department" },
    { label: "Vacancies", key: "vacancies" },
    { label: "Experience Required", key: "experience_required" },
    { label: "Location", key: "location" },
    { label: "Closing Date", key: "closing_date" },
    { label: "Posted Date", key: "created_at" },
    { label: "Status", key: "status" },
  ];

  // If user doesn't have view permission, don't show the page
  if (!permissions.canView) {
    return (
      <div className="flex flex-col gap-2">
        <PageTableHeader breadcrumbs={items} />
        <div className="bg-white p-8 rounded-md text-center">
          <p className="text-gray-500">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <PageTableHeader
          isSearch
          onSearchChange={handleSearchChange}
          add={permissions.canCreate}
          onClickAdd={() => navigate("/recruitment/job-opening/add")}
          breadcrumbs={items}
        />

        <div className="bg-white rounded-lg shadow-sm">
          <CommonTable
            TableHeaderData={TableHeaderData}
            tabledata={tableData.data || []}
            currentPage={currentPage}
            totalData={totalData}
            setCurrentPage={handlePageChange}
            loading={loading}
            action={tableData.action}
          />
        </div>
      </div>

      {/* Beautiful Flowbite Modal */}
      <CommonModal
        show={modal.show}
        headerTitle={
          <div className="flex flex-col items-center gap-4">
            <span className="text-xl text-center font-semibold">
              {modal.title}
            </span>
          </div>
        }
        onClose={() => setModal({ show: false, item: null })}
        modalSize="md"
      >
        <div className="text-center space-y-6 py-4">
          <p className="text-gray-600 text-base">{modal.message}</p>

          <div className="flex justify-center gap-4 pt-4">
            <DefaultButton
              name="Cancel"
              variant="outline"
              size="md"
              onClick={() => setModal({ show: false, item: null })}
              disabled={modal.loading}
            />

            <DefaultButton
              name={modal.confirmText}
              variant={modal.buttonVariant}
              size="md"
              loading={modal.loading}
              onClick={handleStatusConfirm}
            />
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default ManageJobOpening;
